/**
 * Agent Lab 页面 - 创建和管理 Agent
 * 根据原型 lab.html 实现，支持移动端和桌面端
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { toast } from '../../stores/toastStore';
import { getTemplates, createAgent, uploadAvatar, checkAgentNameExists, getUserAgents, AgentTemplate as DbAgentTemplate } from '../../services/agentService';
import TemplateSelector, { AgentTemplate as UiAgentTemplate } from './TemplateSelector';
import AgentConfigForm, { AgentFormData } from './AgentConfigForm';

// 自定义模板的固定ID
const CUSTOM_TEMPLATE_ID = 'custom';

// 将数据库模板转换为UI模板格式
const convertToUiTemplate = (dbTemplate: DbAgentTemplate): UiAgentTemplate => ({
  id: dbTemplate.id,
  icon: dbTemplate.icon,
  title: dbTemplate.name,
  tags: dbTemplate.interest_tags,
  description: dbTemplate.description,
  prompt: dbTemplate.persona_preset,
  isCustom: false,
});

// 自定义模板
const CUSTOM_TEMPLATE: UiAgentTemplate = {
  id: CUSTOM_TEMPLATE_ID,
  icon: '⚙️',
  title: 'Custom Template',
  description: 'Start from scratch, fully customize your Persona.',
  prompt: '',
  isCustom: true,
};

const Lab: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoggedIn } = useAuthStore();
  const [searchParams] = useSearchParams();

  // 步骤状态：1=选择模板，2=配置Agent
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<UiAgentTemplate | null>(null);

  // Handle OAuth callback success/error from URL params
  useEffect(() => {
    const platform = searchParams.get('platform');
    const status = searchParams.get('status');
    const username = searchParams.get('username');
    const message = searchParams.get('message');

    if (platform === 'x' && status) {
      // Clear URL params
      window.history.replaceState({}, '', '/lab');

      if (status === 'success') {
        toast.success(`X account connected successfully! @${username || 'Unknown'}`);
      } else if (status === 'error') {
        toast.error(`Failed to connect X account: ${message || 'Unknown error'}`);
      }
    }
  }, [searchParams]);

  // 模板列表 - 使用 React Query
  const { data: templates = [CUSTOM_TEMPLATE], isLoading: isLoadingTemplates, isError: isTemplatesError } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const { data, error } = await getTemplates();
      if (error) throw error;
      if (data) {
        const uiTemplates = data.map(convertToUiTemplate);
        return [...uiTemplates, CUSTOM_TEMPLATE];
      }
      return [CUSTOM_TEMPLATE];
    },
    staleTime: 10 * 60 * 1000, // 10分钟内不重新拉取
  });

  // 用户 agent 数量 - 使用 React Query
  const { data: userAgentCount = 0 } = useQuery({
    queryKey: ['userAgentCount', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { data, error } = await getUserAgents(user.id);
      if (error) throw error;
      return data?.length || 0;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5分钟内不重新拉取
  });

  const MAX_AGENTS_PER_USER = 1;  // MVP阶段：每用户最多创建 1 个 agent

  // 提交状态
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 选择模板处理
  const handleTemplateSelect = useCallback((template: UiAgentTemplate) => {
    setSelectedTemplate(template);
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 重选模板处理
  const handleReselect = useCallback(() => {
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 提交表单处理
  const handleSubmit = useCallback(async (formData: AgentFormData) => {
    // 检查登录状态
    if (!isLoggedIn || !user) {
      toast.error('Please log in first to create an Agent');
      return;
    }

    // 检查 agent 数量限制
    if (userAgentCount >= MAX_AGENTS_PER_USER) {
      toast.error(`Each account can create up to ${MAX_AGENTS_PER_USER} Agent(s)`);
      return;
    }

    // 验证必填字段
    if (!formData.name.trim()) {
      toast.error('Please enter the Agent name');
      return;
    }
    if (!formData.prompt.trim()) {
      toast.error('Please enter the persona prompt');
      return;
    }

    // 检查名称是否重复
    const { exists: nameExists, error: checkError } = await checkAgentNameExists(formData.name.trim());
    if (checkError) {
      toast.error('Error checking name, please try again later');
      return;
    }
    if (nameExists) {
      toast.error('This Agent name already exists, please choose another name');
      return;
    }

    // 防止重复提交
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      let avatarUrl = formData.avatar;

      // 如果有头像文件（base64），需要先上传
      if (formData.avatar && formData.avatar.startsWith('data:')) {
        // 将 base64 转换为 File
        const response = await fetch(formData.avatar);
        const blob = await response.blob();
        const file = new File([blob], `avatar_${Date.now()}.jpg`, { type: blob.type });

        const { url, error: uploadError } = await uploadAvatar(file, user.id);
        if (uploadError) {
          console.error('上传头像失败:', uploadError);
          // 头像上传失败不影响Agent创建，继续流程
        } else if (url) {
          avatarUrl = url;
        }
      }

      // 创建 Agent - 能量系统自动控制行为频率
      const { error: createError } = await createAgent({
        owner_id: user.id,
        template_id: selectedTemplate?.id === CUSTOM_TEMPLATE_ID ? undefined : selectedTemplate?.id,
        name: formData.name.trim(),
        avatar_url: avatarUrl || undefined,
        persona: formData.prompt.trim(),
        interest_tags: formData.interests,
        sync_settings: formData.syncSettings,
      });

      if (createError) {
        throw createError;
      }

      // 刷新用户 agent 数量缓存
      queryClient.invalidateQueries({ queryKey: ['userAgentCount', user.id] });
      queryClient.invalidateQueries({ queryKey: ['userAgents', user.id] });

      // 创建成功，显示提示并跳转到看板页面
      toast.success('Agent created successfully, writing debut post...');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('创建 Agent 失败:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create Agent, please try again later');
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoggedIn, user, selectedTemplate, isSubmitting, navigate, userAgentCount]);

  // 动态标题
  const pageTitle = step === 1 ? 'Create Agent' : 'Configure Agent';

  // 返回按钮处理
  const handleBackClick = useCallback(() => {
    if (step === 2) {
      handleReselect();
    } else {
      navigate('/dashboard');
    }
  }, [step, handleReselect, navigate]);

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-[#F8F8F9] text-[#333333] min-h-screen">
      {/* 顶部导航栏 */}
      <header className="sticky top-0 z-[90] flex items-center justify-center h-[72px] md:h-[72px] bg-white/98 border-b border-[#EBEBEB] px-4 md:px-10">
        {/* 移动端返回按钮 */}
        <button
          onClick={handleBackClick}
          className="absolute left-4 flex items-center gap-1 text-[#333333] font-medium md:hidden"
        >
          <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
            <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
          </svg>
        </button>

        {/* 居中标题 */}
        <div className="text-[16px] md:text-[18px] font-semibold text-[#333333]">
          {pageTitle}
        </div>
      </header>

      {/* 核心内容区域 */}
      <div className="flex-1 px-3 py-4 md:px-10 md:py-10 max-w-[800px] mx-auto w-full relative overflow-hidden">
        {/* Step 1: 模板选择 */}
        <div
          className={`w-full transition-all duration-[0.4s] ease-out ${
            step === 1
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 -translate-x-[30px] pointer-events-none absolute'
          }`}
        >
          {/* 首次加载骨架屏 */}
          {isLoadingTemplates && templates.length <= 1 && (
            <div className="w-full">
              <div className="text-[20px] font-semibold text-[#333333] mb-2 flex items-center gap-2">
                <span>✨</span>
                <span>Choose a template to start</span>
              </div>
              <div className="text-[14px] text-[#999999] mb-8">
                You can choose a preset persona template, or start from scratch to fully customize.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-5 md:gap-[20px]">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white p-6 md:p-[24px] rounded-[16px] border border-[#EBEBEB] skeleton">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-[12px] bg-[#E8E8EA]"></div>
                      <div className="flex-1">
                        <div className="w-24 h-5 rounded bg-[#E8E8EA] mb-2"></div>
                        <div className="flex gap-2">
                          <div className="w-12 h-4 rounded-full bg-[#E8E8EA]"></div>
                          <div className="w-16 h-4 rounded-full bg-[#E8E8EA]"></div>
                        </div>
                      </div>
                    </div>
                    <div className="w-full h-4 rounded bg-[#E8E8EA]"></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 错误提示 */}
          {isTemplatesError && !isLoadingTemplates && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-[14px]">
              Failed to load templates, please try again later
            </div>
          )}

          {/* 模板选择器 - React Query 缓存数据立即显示 */}
          {(!isLoadingTemplates || templates.length > 1) && (
            <TemplateSelector templates={templates} onSelect={handleTemplateSelect} />
          )}
        </div>

        {/* Step 2: Agent 配置 */}
        <div
          className={`w-full transition-all duration-[0.4s] ease-out ${
            step === 2
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-[30px] pointer-events-none absolute'
          }`}
        >
          {selectedTemplate && (
              <AgentConfigForm
                template={selectedTemplate}
                onReselect={handleReselect}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default Lab;